/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { analytics } from "../../helpers/analytics";
import { trackToolView } from "../../helpers/metaPixel";

import { ToolsModal } from '../ToolsModal';
import { SignIn } from '../SignIn';
import { UserContext } from '../../context/userContext';
import { IBrushFields } from '../../@types/generated/contentful';
import { getContentfulImageSrcSet, getContentfulImageUrl } from '../../helpers/image';

type Props = {
  size: 'large' | 'small';
  set: IBrushFields;
  category: string;
  isFeatured?: boolean;
  isBrushset?: boolean;
};

const ToolCard = ({ size, isBrushset, category, isFeatured, set }: Props) => {
  const [isToolModalOpen, setToolModalOpen] = useState(false);
  const [isAuthOpen, setAuthOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const { user } = useContext(UserContext);
  const { email, hasPremium = false } = user || {};

  const { color, image, section: brushes, title } = set;
  const cardImage = getContentfulImageUrl(image, { width: size === 'large' ? 520 : 360 });
  const cardSrcSet = getContentfulImageSrcSet(image, size === 'large' ? [320, 520, 760] : [240, 360, 520]);

  const openToolModal = () => {
    setToolModalOpen(true);
    setSearchParams({ tool: title ? title.replace(/\s/g, '_') : '' });
    setTimeout(() => {
      analytics.track('Set Selected', {
        premium: hasPremium,
        set: title,
        category,
        featured: isFeatured,
        size,
        brush_count: brushes?.length,
      });
      trackToolView({ title, category, featured: isFeatured });
      // @ts-ignore
      gtag("event", "select_item", {
        'transaction_id': Math.random() * 100000,
        items: [
          {
            item_id: "BrushCardOpened",
            item_name: "Brush card opened",
          }
        ]
      });
    }, 100);
  };

  const handleClick = () => {
    analytics.track("Tool Card Clicked", {
      set: title,
      category,
      featured: isFeatured,
      size,
      is_logged_in: Boolean(email),
      premium: hasPremium,
    });

    if (!email) {
      analytics.track("Auth Required", {
        source: "tool_card",
        set: title,
        category,
      });
      setAuthOpen(true);
      return;
    }

    openToolModal();
  };

  const handleModalClose = () => {
    analytics.track("Tool Modal Closed", {
      set: title,
      category,
    });
    setToolModalOpen(false);
    searchParams.delete('tool');
    setSearchParams(searchParams);
  };

  const handleAuthSuccess = () => {
    setAuthOpen(false);
    analytics.track("Auth Gate Completed", {
      source: "tool_card",
      set: title,
      category,
    });
    openToolModal();
  };

  useEffect(() => {
    if (email && title && searchParams.get('tool') === title.replace(/\s/g, '_')) {
      analytics.track("Tool Deep Link Opened", {
        set: title,
        category,
      });
      setToolModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  return (
    <>
      <div
        className={`
  rounded-[12px] 
  flex
  flex-col
  bg-center
  bg-cover
  cursor-pointer
  relative
  overflow-hidden
  justify-end
  ${
    size === 'large'
      ? 'w-full aspect-square min-w-[0]'
      : 'w-full aspect-square'
  }
  `}
        onClick={handleClick}
        style={{ backgroundColor: color, outline: 'none' }}
      >
        {cardImage && (
          <img
            src={cardImage}
            srcSet={cardSrcSet}
            sizes={size === 'large' ? '(max-width: 480px) 50vw, 260px' : '(max-width: 480px) 170px, 220px'}
            alt=''
            className='absolute z-[0] -translate-x-[50%] -translate-y-[50%] left-1/2 top-1/2 select-none max-h-full'
            loading={isFeatured ? 'eager' : 'lazy'}
            decoding='async'
          />
        )}
        <div
          className={`self-start 
        z-[2] p-5 rounded-[12px] w-full min-h-[48%] flex flex-col justify-end items-start text-white`}
          style={{
            backgroundImage:
              'linear-gradient(to top, rgba(0, 0, 0, 0.86) 0%, rgba(0, 0, 0, 0.56) 58%, rgba(0, 0, 0, 0) 100%)',
            textShadow: '0 2px 10px rgba(0, 0, 0, 0.65)',
          }}
        >
          <h3 className='text-2xl leading-2xl xs:leading-lg xs:text-lg font-bold break-words'>{title}</h3>
          {isBrushset && brushes && (
            <p className='text-xxs leading-xxs text-white/90'>
              {brushes.length} {brushes.length > 1 ? 'brushes' : 'brush'}
            </p>
          )}
        </div>
      </div>
      <ToolsModal isOpen={isToolModalOpen} onClose={handleModalClose} set={set} isBrushset={isBrushset} />
      <SignIn isOpen={isAuthOpen} onClose={() => setAuthOpen(false)} onSuccess={handleAuthSuccess} />
    </>
  );
};

export { ToolCard };
